import React, { FC } from 'react';

import {
  Box,
  Flex,
  Stack,
  Modal,
  Button,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import * as yup from 'yup';

import { Form, Formik } from 'formik';

import { PackageSelect } from '../PackageSelect';

import FormikInput from 'components/primitives/FormikInput';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';

import composeFullAddress from 'utils/composeFullAddress';
import parsePlacesResult, { unitToKeep } from 'utils/parsePlacesResult';

import { EditWashHubModalViewProps } from './EditWashHubModal.props';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Wash hub name is required'),
  email: yup.string().email('Enter a valid email address'),
  address: yup
    .object()
    .typeError('Address is a required field')
    .shape({
      addressLine1: yup.string(),
      addressLine2: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zip: yup
        .string()
        .required('Zip code is a required field')
        .matches(/^[0-9]*$/, 'Invalid format (zip code must be a number).'),
    })
    .required('Address is a required field'),
  packages: yup.array().of(yup.string()),
  phcMethod: yup.number().optional(),
});

const EditWashHubModalView: FC<EditWashHubModalViewProps> = (props) => {
  const { isOpen, onClose, isLoading, onUpdate, selectedHub } = props;

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Edit Wash Hub
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={(values) => onUpdate(values)}
              validationSchema={validationSchema}
              initialValues={{ ...selectedHub }}
              enableReinitialize>
              {({ isValid, values, setFieldValue }) => (
                <Form>
                  <FormikInput
                    name="name"
                    label="Site Name (*)"
                    labelProps={labelProps}
                    containerProps={{ flex: 1, mb: '20px' }}
                  />
                  <FormikSelectInput
                    name="phcMethod"
                    label="Pickup Harvest Counting Method"
                    labelProps={labelProps}
                    defaultValue={(values.phcMethod || 0).toString()}
                    placeholder="Select method">
                    <option value={0}>Rack</option>
                    <option value={1}>Stack</option>
                  </FormikSelectInput>
                  <PlacesAutocomplete
                    name="address"
                    labelText="Address (*)"
                    // Shows the address as stored, unit included. Searching
                    // still geocodes whatever the user types.
                    defaultInputValue={composeFullAddress(values?.address)}
                    labelProps={{ ...labelProps, fontWeight: 'bold' }}
                    onSelect={(result) => {
                      const parsed = parsePlacesResult(result);

                      // addressLine1 is the street; addressLine2 the unit
                      // Google gave, else the one typed below, never a copied
                      // address.
                      setFieldValue('address', {
                        ...parsed,
                        zip: parsed.zip || '00000',
                        addressLine2: unitToKeep(
                          parsed.addressLine2,
                          values?.address?.addressLine2,
                        ),
                      });
                    }}
                  />

                  <Box h="20px" />
                  <FormikInput
                    name="address.addressLine2"
                    label="Address 2"
                    labelProps={labelProps}
                    containerProps={{ flex: 1 }}
                    placeholder="Unit 300"
                    type="text"
                  />

                  <Box h="20px" />
                  <FormikInput
                    name="email"
                    label="Email"
                    labelProps={labelProps}
                    containerProps={{ flex: 1 }}
                    placeholder="hub@example.com"
                    type="email"
                  />

                  <PackageSelect values={values.packages} />
                  <Stack mt={6} align="center" isInline justify="center">
                    <Button
                      variant="outline"
                      w="200px"
                      onClick={() => {
                        onClose();
                      }}
                      isDisabled={isLoading}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      w="200px"
                      isDisabled={!selectedHub || !isValid}
                      isLoading={isLoading}>
                      Update
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditWashHubModalView;
